import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Checkbox } from 'antd';
import { getLang } from 'shared/store/selectors/lang.selector';
import { ListItemsNavigator } from 'src/components/common/items-navigator';
import { Button, Divider } from 'src/components/design-system';
import { usePopoverFocus } from 'src/components/design-system/popover/hooks/use-popover-focus';
import { colorPalette } from 'shared/constants';
import styles from './column-chooser.module.scss';

/**
 * Column Chooser component - allows users to show/hide table columns
 */
export function ColumnChooser({ api, onClose, isOpen }) {
  const lang = useSelector(getLang('FILTERS'));
  const [columns, setColumns] = useState([]);
  const containerRef = useRef(null);

  usePopoverFocus(containerRef, onClose, isOpen);

  // Focus first checkbox when popover opens
  useEffect(() => {
    if (isOpen && columns.length > 0) {
      requestAnimationFrame(() => {
        containerRef.current?.querySelector('input[type="checkbox"]')?.focus();
      });
    }
  }, [isOpen, columns.length]);

  // Load columns from grid
  useEffect(() => {
    if (!api) return;

    const choosableColumns = (api.getColumns() ?? [])
      .filter(col => !col.isPinned() && col.getColDef().colId !== 'settings')
      .map(col => ({
        colId: col.getColId(),
        headerName: col.getColDef().headerName || col.getColId(),
        visible: col.isVisible(),
      }));

    setColumns(choosableColumns);
  }, [api]);

  const onToggleColumn = useCallback(
    colId => {
      const col = columns.find(c => c.colId === colId);
      if (!col) return;
      const newVisible = !col.visible;
      api.setColumnsVisible([colId], newVisible);
      setColumns(prev => prev.map(c => (c.colId === colId ? { ...c, visible: newVisible } : c)));
    },
    [api, columns],
  );

  const onReset = useCallback(() => {
    api.setColumnsVisible(
      columns.map(col => col.colId),
      true,
    );
    setColumns(prev => prev.map(col => ({ ...col, visible: true })));
  }, [api, columns]);

  const hasHiddenColumns = columns.some(col => !col.visible);

  return (
    <div ref={containerRef} className={styles.root}>
      <div className={styles.header}>
        <span className={styles.title}>{lang.COLUMNS}</span>
        <Button
          className={styles.buttonAction}
          size="small"
          onClick={onReset}
          disabled={!hasHiddenColumns}>
          {lang.RESET}
        </Button>
      </div>

      <Divider dividerColor={colorPalette.neutral4} />

      <ListItemsNavigator className={styles.options} itemOverrides={{ role: 'checkbox' }}>
        {columns.map(col => (
          <Checkbox
            aria-label={lang.TOGGLE_COLUMN.replace('{column}', col.headerName || col.colId)}
            key={col.colId}
            checked={col.visible}
            onChange={() => onToggleColumn(col.colId)}
            classNames={{ root: styles.checkbox, label: styles.checkboxLabel }}>
            {col.headerName}
          </Checkbox>
        ))}
      </ListItemsNavigator>
    </div>
  );
}
